import { StatusCode } from "api/protocols";
import { GetTotalBookings } from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { GetTickets } from "api/rpc/2024-04/facilityAdmin/ticket/ticket";
import {
  GetCustomerHouseAccounts,
  GetCustomerNotes,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetCustomerMemberships } from "api/rpc/2024-04/facilityAdmin/customer/membership";
import { GetCustomerFriends } from "api/rpc/2024-04/facilityAdmin/customer/friend";
import { GetCustomerCredit } from "api/rpc/2024-04/facilityAdmin/customer/customerCredit";
import {
  GetLoyaltyCustomer,
  GetLoyaltyCustomerExpiringPoints,
} from "api/rpc/2024-04/facilityAdmin/customer/loyaltyCustomer";
import { GetGiftCard } from "api/rpc/2024-04/facilityAdmin/payment/giftCard";
import { GetItemSalesSummary } from "api/rpc/2024-04/facilityAdmin/order/order";
import { GetReservationBookingAll } from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import { LocaleCurrencyString } from "helpers/Locale";

export type TMergeDataKey =
  | "bookings"
  | "reservations"
  | "memberships"
  | "tickets"
  | "house_accounts"
  | "gift_cards"
  | "orders"
  | "credit_books"
  | "loyalty_points"
  | "notes"
  | "playing_partners";

export type TMergeAccordionItem = {
  id: number | string;
  title: string;
  subtitle?: string;
};

export type TMergeDataCategory = {
  key: TMergeDataKey;
  label: string;
  selected: boolean;
  count?: number;
  summary?: string;
  accordion?: boolean;
  items?: TMergeAccordionItem[];
};

export type TMergeSelections = Record<TMergeDataKey, boolean>;

export const MERGE_DATA_KEYS: TMergeDataKey[] = [
  "bookings",
  "reservations",
  "memberships",
  "tickets",
  "house_accounts",
  "gift_cards",
  "orders",
  "credit_books",
  "loyalty_points",
  "notes",
  "playing_partners",
];

export const DEFAULT_MERGE_SELECTIONS: TMergeSelections = {
  bookings: true,
  reservations: true,
  memberships: true,
  tickets: true,
  house_accounts: true,
  gift_cards: true,
  orders: true,
  credit_books: true,
  loyalty_points: true,
  notes: true,
  playing_partners: true,
};

function money(amount: number | string | null | undefined): string {
  return LocaleCurrencyString({ amount: amount ?? 0 });
}

async function safe<T>(promise: Promise<T>): Promise<T | null> {
  try {
    return await promise;
  } catch {
    return null;
  }
}

export async function loadMergeAccountData(customerId: number): Promise<TMergeDataCategory[]> {
  const [
    bookingsRes,
    reservationsRes,
    membershipsRes,
    ticketsRes,
    houseAccountsRes,
    giftCardsRes,
    ordersRes,
    creditRes,
    loyaltyRes,
    expiringRes,
    notesRes,
    friendsRes,
  ] = await Promise.all([
    safe(GetTotalBookings({ customer_id: customerId }, false)),
    safe(GetReservationBookingAll({ customer_id: customerId }, false)),
    safe(GetCustomerMemberships({ customer_id: customerId }, false)),
    safe(GetTickets({ customer_id: customerId, extended: true }, false)),
    safe(GetCustomerHouseAccounts({ customer_id: customerId }, false)),
    safe(GetGiftCard({ customer_id: customerId }, false)),
    safe(GetItemSalesSummary({ customer_id: customerId }, false)),
    safe(GetCustomerCredit({ customer_id: customerId }, false)),
    safe(GetLoyaltyCustomer({ customer_id: customerId, offset: 0, limit: 1 }, false)),
    safe(GetLoyaltyCustomerExpiringPoints({ customer_id: customerId }, false)),
    safe(GetCustomerNotes({ customer_id: customerId }, false)),
    safe(GetCustomerFriends({ customer_id: customerId }, false)),
  ]);

  let bookingCount = 0;
  let noShows = 0;
  let weather = 0;
  if (bookingsRes?.status === StatusCode.OK && bookingsRes.data) {
    const byYear = bookingsRes.data as Record<
      string,
      { checked?: number; unchecked?: number; no_show_defer?: number; no_show_charge?: number; no_show_weather?: number }
    >;
    Object.values(byYear).forEach(year => {
      bookingCount += Number(year.checked ?? 0) + Number(year.unchecked ?? 0);
      noShows += Number(year.no_show_defer ?? 0) + Number(year.no_show_charge ?? 0);
      weather += Number(year.no_show_weather ?? 0);
    });
  }

  const reservations =
    reservationsRes?.status === StatusCode.OK && Array.isArray(reservationsRes.data) ? reservationsRes.data : [];

  const memberships =
    membershipsRes?.status === StatusCode.OK && Array.isArray(membershipsRes.data) ? membershipsRes.data : [];
  const activeMemberships = memberships.filter((m: { status?: string }) => m.status === "active").length;
  const pastMemberships = memberships.length - activeMemberships;

  const tickets = ticketsRes?.status === StatusCode.OK && Array.isArray(ticketsRes.data) ? ticketsRes.data : [];
  const ticketItems: TMergeAccordionItem[] = tickets.map(
    (ticket: {
      id: number;
      title?: string;
      subtitle?: string;
      stubs?: Array<{ status?: string; parent_id?: number | null }>;
    }) => {
      const activeCount =
        ticket.stubs?.filter(stub => stub.status === "valid" && !stub?.parent_id).length || 0;
      const totalStubs = ticket?.stubs?.filter(stub => !stub?.parent_id)?.length || 0;
      return {
        id: ticket.id,
        title: `${ticket.title || "—"}${totalStubs ? ` (${activeCount}/${totalStubs})` : ""}`,
        subtitle: ticket.subtitle || undefined,
      };
    },
  );

  const houseAccounts =
    houseAccountsRes?.status === StatusCode.OK && Array.isArray(houseAccountsRes.data)
      ? houseAccountsRes.data
      : [];
  const houseAccountItems: TMergeAccordionItem[] = houseAccounts.map(
    (account: { id: number; title?: string; account_number?: string }) => ({
      id: account.id,
      title: account.title || account.account_number || "—",
    }),
  );

  const giftCards =
    giftCardsRes?.status === StatusCode.OK && Array.isArray(giftCardsRes.data) ? giftCardsRes.data : [];

  const orderSummary =
    ordersRes?.status === StatusCode.OK && ordersRes.data
      ? (ordersRes.data as { order_count?: number; total_spend?: number })
      : { order_count: 0, total_spend: 0 };
  const orderCount = Number(orderSummary.order_count ?? 0);
  const totalSpend = Number(orderSummary.total_spend ?? 0);
  const avgSpend = orderCount > 0 ? totalSpend / orderCount : 0;

  const creditBooks =
    creditRes?.status === StatusCode.OK && Array.isArray(creditRes.data) ? creditRes.data : [];
  const creditBalance = creditBooks.reduce(
    (sum: number, book: { balance?: number }) => sum + Number(book.balance ?? 0),
    0,
  );

  const loyaltyCustomer =
    loyaltyRes?.status === StatusCode.OK && Array.isArray(loyaltyRes.data) && loyaltyRes.data.length > 0
      ? loyaltyRes.data[0]
      : null;
  const pointsBalance = Number(loyaltyCustomer?.points_balance ?? 0);
  const expiringPoints =
    expiringRes?.status === StatusCode.OK ? Number(expiringRes.data ?? 0) : 0;

  const notes = notesRes?.status === StatusCode.OK && Array.isArray(notesRes.data) ? notesRes.data : [];
  const highPriorityNotes = notes.filter((note: { high_priority?: boolean }) => note.high_priority).length;
  const regularNotes = notes.length - highPriorityNotes;

  const friends = friendsRes?.status === StatusCode.OK && Array.isArray(friendsRes.data) ? friendsRes.data : [];

  return [
    {
      key: "bookings",
      label: "Bookings",
      selected: true,
      summary: `${bookingCount} bookings • ${noShows} no shows • ${weather} weather`,
    },
    {
      key: "reservations",
      label: "Reservations",
      selected: true,
      count: reservations.length,
    },
    {
      key: "memberships",
      label: "Memberships",
      selected: true,
      summary: `${activeMemberships} active • ${pastMemberships} past`,
    },
    {
      key: "tickets",
      label: "Tickets",
      selected: true,
      count: ticketItems.length,
      accordion: true,
      items: ticketItems,
    },
    {
      key: "house_accounts",
      label: "House accounts",
      selected: true,
      count: houseAccountItems.length,
      accordion: true,
      items: houseAccountItems,
    },
    {
      key: "gift_cards",
      label: "Gift cards",
      selected: true,
      count: giftCards.length,
    },
    {
      key: "orders",
      label: "Orders",
      selected: true,
      summary: `${orderCount} orders • ${money(totalSpend)} spent • ${money(avgSpend)} avg spend per order`,
    },
    {
      key: "credit_books",
      label: "Credit book",
      selected: true,
      summary: `Current balance: ${money(creditBalance as number)}`,
    },
    {
      key: "loyalty_points",
      label: "Loyalty program",
      selected: true,
      summary: `Available points: ${pointsBalance} • Expiring points: ${expiringPoints}`,
    },
    {
      key: "notes",
      label: "Notes",
      selected: true,
      summary: `${highPriorityNotes} high priority notes • ${regularNotes} note${regularNotes === 1 ? "" : "s"}`,
    },
    {
      key: "playing_partners",
      label: "Playing partners",
      selected: true,
      count: friends.length,
    },
  ];
}
